import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import * as Fuse from 'fuse.js';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, startWith, take, takeUntil } from 'rxjs/operators';
import { getFracName } from 'src/app/ui-components/pipes/frac-name.pipe';
import {
  LmoOrdersReplacementDriverOnOrderComponent,
  LmoOrdersReplacementDriverOnOrderDialogResult,
} from '~lmo/components/lmo-orders-replacement/lmo-orders-replacement-driver-on-order/lmo-orders-replacement-driver-on-order.component';
import { Frac, FracClusterTruckingVendor } from '~lmo/models/frac.model';
import { GroupedMeshes, Mesh } from '~lmo/models/mesh.model';
import { FracMine } from '~lmo/models/mine.model';
import { Report } from '~lmo/models/report.model';
import { LmoOrdersService } from '~lmo/services';
import { LmoFracFcpoService } from '~lmo/services/lmo-frac-fcpo.service';
import { LmoFracsService } from '~lmo/services/lmo-fracs.service';
import { ReplacementOrderRequest } from '~lmo/services/lmo-orders.service';
import { LmoReportsService } from '~lmo/services/lmo-reports.service';
import { ReplaceableOrder } from '~models/order.model';
import { Subcarrier } from '~models/subcarrier.model';
import { Truck } from '~models/truck.model';
import { ReplacementDriver } from '~models/user';
import { SearchApiService } from '~services/api/search.api.service';
import { UserApiService } from '~services/api/user.api.service';
import { SubcarrierService } from '~services/subcarrier.service';
import { trackById } from '~utilities/trackById';
import { FracClusterPurchaseOrder, PurchaseOrder } from '~v2Models/purchaseOrders.model';
import { TrucksService } from '../../../trucks.service';

const driverSearchOptions: Fuse.FuseOptions<ReplacementDriver> = {
  distance: 100,
  keys: ['name', 'phone'],
  location: 0,
  maxPatternLength: 9,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

const subcarrierSearchOptions: Fuse.FuseOptions<Subcarrier> = {
  distance: 100,
  keys: ['name'],
  location: 0,
  maxPatternLength: 9,
  minMatchCharLength: 1,
  shouldSort: true,
  threshold: 0.2,
};

interface ReplacementOrderForm {
  loader: FracMineWithPoInformation;
  meshId: number;
  pickupTime: Date;
  truckingVendor: FracClusterTruckingVendor;
  driver: ReplacementDriver;
  bolNumber: string;
  ticketNumber?: string;
  truckId: Truck;
  finalLoadWeight?: number;
  boxOrder: boolean;
  box1Name?: string;
  box1Weight?: number;
  box2Name?: string;
  box2Weight?: number;
  completeReplacementOrder: boolean;
  nonBillableOrder: boolean;
  replacedOrder?: ReplaceableOrder;
  brokerTrailerTypeId?: number;
  driverSubcarrierId?: number;
}

interface FracMineWithPoInformation extends FracMine {
  fcpo: FracClusterPurchaseOrder;
  downReport?: Report;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-orders-replacement',
  styleUrls: ['./lmo-orders-replacement.component.scss'],
  templateUrl: './lmo-orders-replacement.component.html',
})
export class LmoOrdersReplacementComponent implements OnInit, OnDestroy {
  public networkActive$$ = new BehaviorSubject<boolean>(false);
  public destroy$$ = new Subject();
  public replacementOrderForm: FormGroup;
  public currentFrac$: Observable<Frac>;
  public currentFracName$: Observable<string>;
  public meshesInGroup$: Observable<GroupedMeshes[]>;
  public fracMines$: Observable<FracMineWithPoInformation[]>;
  public truckingVendors$: Observable<FracClusterTruckingVendor[]>;
  public eligibleReplacementDrivers$$ = new BehaviorSubject<ReplacementDriver[]>([]);
  public eligibleReplaceableOrders$$ = new BehaviorSubject<ReplaceableOrder[]>([]);
  public filteredDrivers$: Observable<ReplacementDriver[]>;
  public filterDriver = new FormControl();

  public selectedVendorIsBrokerage$$ = new BehaviorSubject<boolean>(false);

  public isBoxStorage: boolean;
  public isTwoBoxes$$ = new BehaviorSubject<boolean>(false);

  public brokerTrailerTypes$ = this.subcarrierService.brokerTrailerTypes$;

  public allSubcarriers$ = this.subcarrierService.subcarriersForLMO$;
  public subcarriersForBroker$: Observable<Subcarrier[]>;
  public filteredSubcarriers$$ = new BehaviorSubject<Subcarrier[]>([]);
  public filterSubcarrier = new FormControl();

  public selectedTruck: any;
  public trackById = trackById;

  private get formValue(): ReplacementOrderForm {
    return this.replacementOrderForm.value;
  }

  constructor(
    private orderService: LmoOrdersService,
    private fracService: LmoFracsService,
    private fb: FormBuilder,
    private matDialogRef: MatDialogRef<LmoOrdersReplacementComponent, any>,
    private fcpoService: LmoFracFcpoService,
    private reportService: LmoReportsService,
    private userApiService: UserApiService,
    private router: Router,
    private snackbar: MatSnackBar,
    private matDialog: MatDialog,
    private cd: ChangeDetectorRef,
    private subcarrierService: SubcarrierService,
    public truckService: TrucksService,
    private searchApiService: SearchApiService,
  ) {}

  public async ngOnInit() {
    this.subcarrierService.getSubcarriersForLMO();

    this.currentFrac$ = this.fracService.currentFrac$.pipe(filter((frac) => !!frac));
    this.currentFracName$ = this.currentFrac$.pipe(map(getFracName));

    const currentFrac = await this.currentFrac$.pipe(take(1)).toPromise();

    this.isBoxStorage = currentFrac.fracStorages?.[0]?.storageType?.name === 'Box';

    const commonControls = {
      bolNumber: [null, [Validators.required]],
      brokerTrailerTypeId: [null, []],
      completeReplacementOrder: [false, [Validators.required]],
      driver: this.fb.control({ value: null, disabled: true }, [Validators.required]),
      driverSubcarrierId: [null, []],
      loader: this.fb.control({ value: null, disabled: true }, [Validators.required]),
      meshId: [null, [Validators.required]],
      nonBillableOrder: [false, [Validators.required]],
      replacedOrder: [{ value: null, disabled: true }, []],
      ticketNumber: [null, []],
      truckId: [null, []],
      truckingVendor: [null, [Validators.required]],
    };

    if (this.isBoxStorage) {
      this.replacementOrderForm = this.fb.group({
        ...commonControls,
        box1Name: [null, [Validators.required]],
        box1Weight: [null, [Validators.required]],
        box2Name: [null, []],
        box2Weight: [null, []],
      });
    } else {
      this.replacementOrderForm = this.fb.group({
        ...commonControls,
        finalLoadWeight: [null, [Validators.required]],
      });
    }

    this.setupSearch();
    this.setupFormDisableAndEnableListeners();
    this.setupListenersForFormOptions();
    this.setupFormRequiredListeners();

    this.replacementOrderForm.controls['truckId'].valueChanges
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe((searchTerm) => this.truckService.searchTruck(searchTerm));

    this.cd.markForCheck();
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.unsubscribe();
  }

  public trackByFCPOId(fcpo: FracMineWithPoInformation) {
    return fcpo;
  }

  public trackByVendorId(truckingVendor: FracClusterTruckingVendor) {
    return truckingVendor.id;
  }

  public async replaceOrder() {
    if (this.replacementOrderForm.invalid || this.networkActive$$.value) {
      return;
    }

    try {
      this.networkActive$$.next(true);

      const formValue = this.formValue;

      const currentFracId = await this.fracService.currentFrac$
        .pipe(
          take(1),
          map((frac) => frac.id),
        )
        .toPromise();

      let numBoxes = 0;
      if (this.isBoxStorage) {
        numBoxes = 1;
        if (this.isTwoBoxes$$?.value) {
          numBoxes = 2;
        }
      }

      let currentOrderAction: string;

      if (!formValue.completeReplacementOrder && formValue.driver.currentOrderId) {
        const currentOrderDialog = this.matDialog.open(LmoOrdersReplacementDriverOnOrderComponent, {
          data: {
            driver: formValue.driver,
          },
        });

        const result: LmoOrdersReplacementDriverOnOrderDialogResult = await currentOrderDialog
          .afterClosed()
          .toPromise();

        if (!result.shouldSubmit) {
          return;
        }

        if (result.shouldComplete) {
          currentOrderAction = 'complete';
        } else {
          currentOrderAction = 'cancel';
        }
      }

      const replacementOrder: ReplacementOrderRequest = {
        bolNumber: formValue.bolNumber,
        box1Name: numBoxes >= 1 ? formValue.box1Name : null,
        box1Weight: numBoxes >= 1 ? formValue.box1Weight : null,
        box2Name: numBoxes >= 2 ? formValue.box2Name : null,
        box2Weight: numBoxes >= 2 ? formValue.box2Weight : null,
        brokerTrailerTypeId: formValue.brokerTrailerTypeId,
        completeReplacementOrder: formValue.completeReplacementOrder,
        driverCurrentOrderAction: currentOrderAction,
        driverId: formValue.driver.id,
        driverSubcarrierId: formValue.driverSubcarrierId,
        finalLoadWeight: formValue.finalLoadWeight,
        fracId: currentFracId,
        meshId: formValue.meshId,
        mineId: formValue.loader.id,
        nonBillableOrder: formValue.nonBillableOrder,
        numBoxes: numBoxes,
        purchaseOrderId: formValue.loader.fcpo.purchaseOrder.id,
        replacedOrderId: formValue.replacedOrder?.orderId,
        ticketNumber: formValue.ticketNumber,
        truckId: formValue.truckId?.id,
        truckingVendorId: formValue.truckingVendor?.truckingVendorId,
      };

      const newOrder = await this.orderService.sendReplacementRequest$(replacementOrder);

      if (newOrder !== null) {
        this.router.navigate(['/', 'lmo', 'frac', currentFracId, 'orders', newOrder.id]);
        this.snackbar.open('Replacement order created', null, { duration: 3000 });
        this.matDialogRef.close();
      }
    } finally {
      this.networkActive$$.next(false);
    }
  }

  public isMaxedOut(po: PurchaseOrder): boolean {
    if (!po) {
      return false;
    }
    return (po.orderedToday || 0) > po.maximumDailyAllocation || (po.ordered || 0) + (po.pickedUp || 0) > po.allocation;
  }

  private setupFormRequiredListeners() {
    combineLatest([
      this.replacementOrderForm.get('meshId').valueChanges.pipe(takeUntil(this.destroy$$)),
      this.currentFrac$.pipe(takeUntil(this.destroy$$)),
    ])
      .pipe(takeUntil(this.destroy$$))
      .subscribe(([meshId, currentFrac]) => {
        if (!currentFrac) {
          return;
        }

        const storage = currentFrac.fracStorages.find((fs) => {
          if (fs.mesh?.id === meshId) {
            return true;
          }

          return fs.supplementalMeshes?.some((sm) => sm.meshId === meshId);
        });

        if (storage?.numberBoxes === 1) {
          this.isTwoBoxes$$.next(false);
          this.replacementOrderForm.get('box2Weight').setValidators([]);
          this.replacementOrderForm.get('box2Name').setValidators([]);
          this.replacementOrderForm.get('box2Weight').updateValueAndValidity();
          this.replacementOrderForm.get('box2Name').updateValueAndValidity();
        } else {
          this.isTwoBoxes$$.next(true);
          this.replacementOrderForm.get('box2Weight').setValidators([Validators.required]);
          this.replacementOrderForm.get('box2Name').setValidators([Validators.required]);
          this.replacementOrderForm.get('box2Weight').updateValueAndValidity();
          this.replacementOrderForm.get('box2Name').updateValueAndValidity();
        }
      });
  }

  private setupSearch() {
    this.filteredDrivers$ = combineLatest([
      this.eligibleReplacementDrivers$$,
      this.filterDriver.valueChanges.pipe(startWith(null as any), distinctUntilChanged()),
    ]).pipe(
      map(([drivers, searchValue]) => {
        if (!searchValue) {
          return drivers;
        } else {
          return new Fuse(drivers, driverSearchOptions).search(searchValue) as any;
        }
      }),
    );

    this.subcarriersForBroker$ = combineLatest([
      this.replacementOrderForm
        .get('truckingVendor')
        .valueChanges.pipe(takeUntil(this.destroy$$), distinctUntilChanged()),
      this.allSubcarriers$,
    ]).pipe(
      map(([truckingVendor, subcarriers]) => {
        if (truckingVendor?.isBrokerage) {
          return subcarriers.filter((sc) => sc.brokerId === truckingVendor.truckingVendorId);
        } else {
          return [];
        }
      }),
    );

    combineLatest([
      this.subcarriersForBroker$,
      this.filterSubcarrier.valueChanges.pipe(startWith(null as any), distinctUntilChanged()),
    ])
      .pipe(takeUntil(this.destroy$$))
      .subscribe(([subcarriers, searchValue]) => {
        if (!searchValue) {
          this.filteredSubcarriers$$.next(subcarriers);
        } else {
          this.filteredSubcarriers$$.next(new Fuse(subcarriers, subcarrierSearchOptions).search(searchValue) as any);
        }
      });
  }

  private setupFormDisableAndEnableListeners() {
    this.replacementOrderForm
      .get('meshId')
      .valueChanges.pipe(takeUntil(this.destroy$$), distinctUntilChanged())
      .subscribe((meshId) => {
        if (!meshId) {
          this.replacementOrderForm.get('loader').disable();
        } else {
          this.replacementOrderForm.get('loader').enable();
        }
      });

    this.replacementOrderForm
      .get('truckingVendor')
      .valueChanges.pipe(takeUntil(this.destroy$$), distinctUntilChanged())
      .subscribe((truckingVendor) => {
        this.replacementOrderForm.get('driver').setValue(null);
        this.replacementOrderForm.get('driver').disable();
        this.replacementOrderForm.get('driver').updateValueAndValidity();
        this.cd.markForCheck();
        this.eligibleReplacementDrivers$$.next([]);
        if (truckingVendor?.truckingVendorId) {
          this.selectedVendorIsBrokerage$$.next(truckingVendor.isBrokerage);

          this.userApiService.getEligibleReplacementDrivers$(truckingVendor.truckingVendorId).subscribe((drivers) => {
            this.eligibleReplacementDrivers$$.next(drivers);
            this.replacementOrderForm.get('driver').enable();
          });
        } else {
          this.selectedVendorIsBrokerage$$.next(false);
        }
      });

    combineLatest([
      this.replacementOrderForm
        .get('truckingVendor')
        .valueChanges.pipe(takeUntil(this.destroy$$), distinctUntilChanged()),
      this.replacementOrderForm.get('driver').valueChanges.pipe(takeUntil(this.destroy$$), distinctUntilChanged()),
    ])
      .pipe(takeUntil(this.destroy$$))
      .subscribe(([truckingVendor, driver]) => {
        this.replacementOrderForm.get('replacedOrder').disable();
        this.replacementOrderForm.get('replacedOrder').setValue(null);
        this.eligibleReplaceableOrders$$.next([]);
        if (truckingVendor?.truckingVendorId && driver) {
          this.orderService
            .getOrdersEligibleForReplacement$(truckingVendor?.truckingVendorId, driver.id)
            .subscribe((orders) => {
              this.eligibleReplaceableOrders$$.next(orders);
              this.replacementOrderForm.get('replacedOrder').enable();
            });
        }
      });

    combineLatest([
      this.replacementOrderForm
        .get('truckingVendor')
        .valueChanges.pipe(takeUntil(this.destroy$$), distinctUntilChanged()),
      this.replacementOrderForm.get('driver').valueChanges.pipe(takeUntil(this.destroy$$), distinctUntilChanged()),
    ])
      .pipe(takeUntil(this.destroy$$))
      .subscribe(([truckingVendor, driver]) => {
        if (!truckingVendor || !driver) {
          this.replacementOrderForm.get('brokerTrailerTypeId').disable();
          this.replacementOrderForm.get('brokerTrailerTypeId').setValue(null);
          this.replacementOrderForm.get('brokerTrailerTypeId').updateValueAndValidity();

          this.replacementOrderForm.get('driverSubcarrierId').disable();
          this.replacementOrderForm.get('driverSubcarrierId').setValue(null);
          this.replacementOrderForm.get('driverSubcarrierId').updateValueAndValidity();

          return;
        }

        this.replacementOrderForm.get('brokerTrailerTypeId').enable();
        this.replacementOrderForm.get('brokerTrailerTypeId').setValue(driver?.brokerTrailerTypeId);
        this.replacementOrderForm.get('brokerTrailerTypeId').updateValueAndValidity();

        this.replacementOrderForm.get('driverSubcarrierId').enable();
        this.replacementOrderForm.get('driverSubcarrierId').setValue(driver?.subcarrierId);
        this.replacementOrderForm.get('driverSubcarrierId').updateValueAndValidity();
      });
  }

  private setupListenersForFormOptions() {
    this.meshesInGroup$ = this.currentFrac$.pipe(
      map((frac) => {
        const uniqueMeshRecord = (frac.fracStorages || []).reduce((meshRecord, storage) => {
          meshRecord[storage.id] = {
            meshName: storage.mesh.type,
            meshes: [storage.mesh],
            storageId: storage.id,
          } as GroupedMeshes;
          return meshRecord;
        }, {});

        for (const fracStorage of frac.fracStorages) {
          for (const mesh of fracStorage.supplementalMeshes || []) {
            const supplementalMesh: Mesh = {
              id: mesh.meshId,
              isSupplemental: true,
              preferredOrder: 0,
              type: mesh.meshName,
            };
            uniqueMeshRecord[fracStorage.id].meshes.push(supplementalMesh);
          }
        }
        return Object.keys(uniqueMeshRecord).map((key) => uniqueMeshRecord[key]);
      }),
    );

    this.fracMines$ = combineLatest([
      this.replacementOrderForm.get('meshId').valueChanges.pipe(startWith(-1)),
      this.currentFrac$,
      this.fcpoService.currentFracFcpos$,
      this.reportService.reports$,
    ]).pipe(
      map(([selectedMesh, currentFrac, fcpos, reports]) => {
        const validMineIds = fcpos
          .map((fcpo) => fcpo.purchaseOrder)
          .filter((po) => po.meshId === selectedMesh)
          .map((po) => po.mineId);
        const fracMines = [...(currentFrac.fracMines || [])];
        const fracMinesWithPoInfo: FracMineWithPoInformation[] = [];
        fracMines.forEach((fracMine) => {
          const mineFCPOs = fcpos.filter(
            (fcpo) =>
              fcpo.purchaseOrder &&
              fcpo.purchaseOrder.mineId === fracMine.id &&
              fcpo.purchaseOrder.meshId === selectedMesh &&
              fcpo.purchaseOrder.pausedTimestamp === null &&
              !fcpo.purchaseOrder.closed,
          );
          mineFCPOs.forEach((fcpo) => {
            const downReport = reports.find(
              (r) => !r.available && r.site.id === fracMine.site.id && r.mesh && r.mesh.id === selectedMesh,
            );
            fracMinesWithPoInfo.push({
              ...fracMine,
              downReport,
              fcpo,
            });
          });
        });
        return fracMinesWithPoInfo.filter((fracMine) => validMineIds.includes(fracMine.id));
      }),
    );

    this.truckingVendors$ = this.currentFrac$.pipe(
      map((frac) => (frac.fracClusterTruckingVendors || []).filter((fctv) => fctv.status === 'awarded')),
    );
  }

  public calculatePoRemaining(po: PurchaseOrder): number {
    const remaining = po.allocation - (po.pickedUp || 0);
    if (remaining < 0) {
      return 0;
    }
    return remaining;
  }

  public clearTruck() {
    if (
      (this.selectedTruck &&
        this.selectedTruck.name !== this.replacementOrderForm.controls['truckId'].value &&
        this.selectedTruck.name !== this.replacementOrderForm.controls['truckId'].value.name) ||
      !this.selectedTruck
    ) {
      this.replacementOrderForm.controls['truckId'].setValue(null);
    }
  }

  public displayTruckFn(truck?: Truck): string | undefined {
    return truck ? truck.name : undefined;
  }

  public onTruckSelected(truck: Truck | string) {
    if (typeof truck === 'string') {
      this.searchApiService.createTruck(truck).subscribe((_) => {
        this.replacementOrderForm.controls['truckId'].patchValue(_);
        this.selectedTruck = _;
      });
    } else {
      this.replacementOrderForm.controls['truckId'].setValue(truck);
      this.selectedTruck = truck;
    }
  }

  public checkForAddTruck$(): Observable<boolean> {
    if (!this.replacementOrderForm.controls['truckId'].value) {
      return of(false);
    }
    const truckName = this.replacementOrderForm.controls['truckId'].value.name;
    return this.truckService.truckList$.pipe(map((truckList) => truckList.every((truck) => truck.name !== truckName)));
  }
}
